import React, { useState, useEffect } from 'react';
import { Meal, MealType } from '../lib/types';
import { formatIsoDate, getDefaultMealType, createIsoFromLocalDateAndTime } from '../lib/dateUtils';
import { canonicalizeIngredient, decomposeCompositeDish, extractRecipeServingsAndPortion } from '../lib/ingredientUtils';
import { localStore } from '../lib/storage';
import { autoRegisterDiscoveredFoods } from '../lib/fodmapEngine';
import { describeMealDirectOrProxy } from '../lib/gemini';
import {
  X,
  MessageSquare,
  Sparkles,
  Loader2,
  Clock,
  Calendar,
  Check,
  Plus,
  Sunrise,
  Sun,
  Moon,
  Coffee,
  Apple,
} from 'lucide-react';

interface DescribeLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveMeal: (meal: Omit<Meal, 'id'>) => void;
}

const MEAL_TYPES: { type: MealType; label: string; icon: React.FC<{ className?: string }> }[] = [
  { type: 'Breakfast', label: 'Breakfast', icon: Sunrise },
  { type: 'Lunch', label: 'Lunch', icon: Sun },
  { type: 'Dinner', label: 'Dinner', icon: Moon },
  { type: 'Snack', label: 'Snack', icon: Apple },
  { type: 'Drink', label: 'Drink', icon: Coffee },
];

export const DescribeLogModal: React.FC<DescribeLogModalProps> = ({
  isOpen,
  onClose,
  onSaveMeal,
}) => {
  const [mealType, setMealType] = useState<MealType>(() => getDefaultMealType());
  const [descriptionPrompt, setDescriptionPrompt] = useState('');
  const [dishName, setDishName] = useState('');
  const [recipeServings, setRecipeServings] = useState<number>(1);
  const [portionsEaten, setPortionsEaten] = useState<number>(1);
  const [ingredients, setIngredients] = useState<string[]>([]);
  const [newIngredientInput, setNewIngredientInput] = useState('');
  const [notes, setNotes] = useState('');

  const now = new Date();
  const [entryDate, setEntryDate] = useState(() => formatIsoDate(now));
  const [entryTime, setEntryTime] = useState(() => {
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  });

  const [isDecoding, setIsDecoding] = useState(false);
  const [decodeNotice, setDecodeNotice] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setMealType(getDefaultMealType());
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDecodeDescription = async () => {
    const text = descriptionPrompt.trim();
    if (!text) return;

    // Detect natural servings if present
    const extracted = extractRecipeServingsAndPortion(text);
    if (extracted) {
      if (extracted.recipeServings) setRecipeServings(extracted.recipeServings);
      if (extracted.portionsEaten) setPortionsEaten(extracted.portionsEaten);
    }

    setIsDecoding(true);
    setDecodeNotice('AI decoding meal into ingredients...');

    try {
      let parsed: any = null;
      const directResult = await describeMealDirectOrProxy(text);
      if (directResult.success && directResult.data) {
        parsed = directResult.data;
      }

      // If network unreachable, fallback gracefully to offline culinary dictionary
      if (!parsed || (!parsed.dish_name && !parsed.ingredients)) {
        const decomp = decomposeCompositeDish(text);
        if (decomp) {
          parsed = {
            dish_name: decomp.matchedDish || text,
            meal_category: decomp.category || getDefaultMealType(),
            ingredients: decomp.ingredients,
          };
        }
      }

      if (parsed) {
        // Auto-register any novel ingredients discovered by AI into local & global database
        if (Array.isArray(parsed.discovered_foods) && parsed.discovered_foods.length > 0) {
          autoRegisterDiscoveredFoods(parsed.discovered_foods).catch(() => {});
        } else if (Array.isArray(parsed.ingredients) && parsed.ingredients.length > 0) {
          autoRegisterDiscoveredFoods(parsed.ingredients).catch(() => {});
        }

        if (parsed.dish_name) {
          setDishName(parsed.dish_name);
        } else {
          setDishName(text.replace(/\b\w/g, (c) => c.toUpperCase()));
        }

        if (parsed.meal_category && ['Breakfast', 'Lunch', 'Dinner', 'Snack', 'Drink'].includes(parsed.meal_category)) {
          setMealType(parsed.meal_category);
        }

        let rawList: string[] = Array.isArray(parsed.ingredients) ? parsed.ingredients : [];
        // If parsed list is empty or only has the composite title itself, expand with culinary recipe decomposition
        if (rawList.length <= 1) {
          const decomp = decomposeCompositeDish(text);
          if (decomp && decomp.ingredients.length > rawList.length) {
            rawList = decomp.ingredients;
            if (decomp.category && !parsed.meal_category) {
              setMealType(decomp.category);
            }
          }
        }

        if (rawList.length > 0) {
          const cleanList = rawList
            .filter((t: string) => !/may contain|trace/i.test(t))
            .filter((t: string) => !/^(water|tap water|salt|table salt|sea salt|black pepper|pepper|cooking spray)$/i.test(t.trim()))
            .map((t: string) => canonicalizeIngredient(t))
            .filter(Boolean);
          setIngredients(Array.from(new Set(cleanList)));
          setDecodeNotice(`Decoded ${cleanList.length} ingredient${cleanList.length === 1 ? '' : 's'}.`);
        } else {
          setDecodeNotice('Dish identified. Tap "+ Add" to add any custom ingredients.');
        }
      } else {
        throw new Error('AI parsing produced no structured data');
      }
    } catch (err) {
      console.warn('Decode fallback activated:', err);
      // 1. Check culinary composite dish knowledge base first
      const decomp = decomposeCompositeDish(text);
      if (decomp) {
        setDishName(decomp.matchedDish || text.replace(/\b\w/g, (c) => c.toUpperCase()));
        if (decomp.category) setMealType(decomp.category);
        setIngredients(decomp.ingredients);
        setDecodeNotice(`Extracted ${decomp.ingredients.length} ingredients from recipe decomposition.`);
      } else {
        setDishName(text.replace(/\b\w/g, (c) => c.toUpperCase()));
        const fallbackSegments = text
          .split(/,|\band\b|\bwith\b|\b&\b|\+/i)
          .map((s) => s.trim())
          .filter((s) => s.length >= 2 && s.length <= 30)
          .map((s) => canonicalizeIngredient(s));
        const cleanList = Array.from(new Set(fallbackSegments.filter(Boolean)));
        if (cleanList.length > 0) {
          setIngredients(cleanList);
          setDecodeNotice(`Extracted ${cleanList.length} ingredients from description.`);
        } else {
          setDecodeNotice('Set dish title. You can add ingredients below.');
        }
      }
    } finally {
      setIsDecoding(false);
    }
  };

  const handleAddIngredient = () => {
    const trimmed = newIngredientInput.trim();
    if (!trimmed) return;
    const canonical = canonicalizeIngredient(trimmed);
    if (!ingredients.includes(canonical)) {
      setIngredients([...ingredients, canonical]);
    }
    setNewIngredientInput('');
  };

  const handleRemoveIngredient = (ing: string) => {
    setIngredients(ingredients.filter((i) => i !== ing));
  };

  const handleResetInputs = () => {
    setDescriptionPrompt('');
    setDishName('');
    setIngredients([]);
    setNewIngredientInput('');
    setNotes('');
    setDecodeNotice(null);
    const now = new Date();
    setEntryDate(formatIsoDate(now));
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    setEntryTime(`${hh}:${mm}`);
    setMealType(getDefaultMealType());
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const finalDescription = dishName.trim() || descriptionPrompt.trim();
    if (!finalDescription) return;

    const mult = Math.max(0.01, Math.round((portionsEaten / recipeServings) * 1000) / 1000);

    onSaveMeal({
      date: entryDate,
      timestamp_iso: createIsoFromLocalDateAndTime(entryDate, entryTime),
      meal_type: mealType,
      description: finalDescription,
      portionMultiplier: mult,
      recipeServings: recipeServings,
      portionsEaten: portionsEaten,
      ingredients_tags: ingredients.join(', '),
      notes: notes.trim() || undefined,
    });

    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex flex-col justify-end animate-fade-in font-[-apple-system,BlinkMacSystemFont,'SF_Pro_Text',sans-serif]">
      <div className="bg-white text-slate-900 rounded-t-[32px] sm:rounded-2xl max-h-[92vh] flex flex-col w-full max-w-md mx-auto border-t sm:border border-slate-200 shadow-2xl overflow-hidden">
        {/* Drag Handle */}
        <div className="w-full pt-3 pb-1 flex justify-center bg-white">
          <div className="w-10 h-1.5 rounded-full bg-slate-300" />
        </div>

        {/* Header */}
        <div className="px-6 py-2.5 flex items-center justify-between border-b border-slate-100 bg-white">
          <div className="flex items-center space-x-2">
            <MessageSquare className="w-4 h-4 text-slate-900" />
            <h2 className="text-base font-black uppercase tracking-tight text-slate-900">Describe Meal</h2>
          </div>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={handleResetInputs}
              className="text-[11px] font-bold text-slate-400 hover:text-rose-500 transition-colors px-1.5 py-1"
            >
              Reset Inputs
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-700 hover:text-rose-500 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-5 overflow-y-auto space-y-4 flex-1 text-left bg-white">
          {/* Description Prompt & AI Decode */}
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">
              What did you eat or drink?
            </label>
            <div className="space-y-2">
              <textarea
                rows={2}
                placeholder="e.g., Chicken burrito bowl with brown rice, black beans, salsa, and avocado..."
                value={descriptionPrompt}
                onChange={(e) => setDescriptionPrompt(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold text-slate-900 focus:outline-none focus:border-slate-400 resize-none"
              />
              <button
                type="button"
                disabled={!descriptionPrompt.trim() || isDecoding}
                onClick={handleDecodeDescription}
                className="w-full py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-black flex items-center justify-center space-x-1.5 active:scale-[0.98] disabled:opacity-40 transition-all shadow-xs"
              >
                {isDecoding ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5" />
                )}
                <span>{isDecoding ? 'Decoding Ingredients...' : 'Decode Description'}</span>
              </button>
            </div>

            {decodeNotice && (
              <p className="text-[10px] text-slate-500 font-medium px-1">
                {decodeNotice}
              </p>
            )}
          </div>

          {/* Dish Name */}
          {dishName && (
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                Dish Title
              </label>
              <input
                type="text"
                value={dishName}
                onChange={(e) => setDishName(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-white dark:bg-zinc-800/80 border border-slate-200 dark:border-zinc-800 rounded-xl text-xs font-bold text-slate-950 dark:text-white focus:outline-none"
              />
            </div>
          )}

          {/* Meal Category Pills (Auto-selected, user can change) */}
          <div className="space-y-1.5">
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500 block">
              Meal Category (Auto-Selected)
            </span>
            <div className="grid grid-cols-5 gap-1 p-1 bg-slate-100 dark:bg-zinc-900 rounded-xl border border-slate-200/80 dark:border-zinc-800">
              {MEAL_TYPES.map(({ type, label, icon: Icon }) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setMealType(type)}
                  className={`py-1.5 px-1 rounded-lg text-xs font-bold flex flex-col items-center justify-center space-y-0.5 transition-all ${
                    mealType === type
                      ? 'bg-slate-950 dark:bg-white text-white dark:text-black shadow-xs font-black'
                      : 'text-slate-500 dark:text-zinc-400 hover:text-slate-900'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span className="text-[9px]">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Recipe Yield & Portion Eaten Controls */}
          <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200 space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                Recipe Yield & Portion Eaten
              </span>
              <span className="text-[10px] font-bold text-slate-700">
                {portionsEaten} of {recipeServings} {recipeServings === 1 ? 'serv' : 'servs'} ({Math.round((portionsEaten / recipeServings) * 1000) / 10}%)
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 mb-1">
                  Recipe Serves
                </label>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setRecipeServings(Math.max(1, recipeServings - 1))}
                    className="w-7 h-7 rounded-lg bg-white border border-slate-200 text-slate-800 font-bold flex items-center justify-center hover:bg-slate-100"
                  >
                    -
                  </button>
                  <span className="flex-1 text-center font-bold text-slate-900 text-xs">
                    {recipeServings} {recipeServings === 1 ? 'serv' : 'servs'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setRecipeServings(recipeServings + 1)}
                    className="w-7 h-7 rounded-lg bg-white border border-slate-200 text-slate-800 font-bold flex items-center justify-center hover:bg-slate-100"
                  >
                    +
                  </button>
                </div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {[1, 2, 4, 6, 8].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setRecipeServings(s)}
                      className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-colors ${
                        recipeServings === s
                          ? 'bg-slate-900 text-white'
                          : 'bg-white border border-slate-200 hover:bg-slate-100 text-slate-700'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 mb-1">
                  Portion You Ate
                </label>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setPortionsEaten(Math.max(0.25, Math.round((portionsEaten - 0.5) * 100) / 100))}
                    className="w-7 h-7 rounded-lg bg-white border border-slate-200 text-slate-800 font-bold flex items-center justify-center hover:bg-slate-100"
                  >
                    -
                  </button>
                  <span className="flex-1 text-center font-bold text-slate-900 text-xs">
                    {portionsEaten} {portionsEaten === 1 ? 'portion' : 'portions'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPortionsEaten(Math.round((portionsEaten + 0.5) * 100) / 100)}
                    className="w-7 h-7 rounded-lg bg-white border border-slate-200 text-slate-800 font-bold flex items-center justify-center hover:bg-slate-100"
                  >
                    +
                  </button>
                </div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {[0.5, 1, 1.5, 2, recipeServings].filter((v, idx, arr) => arr.indexOf(v) === idx).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPortionsEaten(p)}
                      className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-colors ${
                        portionsEaten === p
                          ? 'bg-slate-900 text-white'
                          : 'bg-white border border-slate-200 hover:bg-slate-100 text-slate-700'
                      }`}
                    >
                      {p === recipeServings && p > 1 ? 'All' : `${p}`}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Simple Ingredients Selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                Ingredients ({ingredients.length})
              </label>
              <span className="text-[9px] text-slate-400">Tap to remove</span>
            </div>

            {/* Ingredients Tags */}
            <div className="flex flex-wrap gap-1.5 min-h-[32px] p-2.5 bg-slate-50 rounded-2xl border border-slate-200">
              {ingredients.map((ing) => (
                <button
                  key={ing}
                  type="button"
                  onClick={() => handleRemoveIngredient(ing)}
                  className="px-2.5 py-1 rounded-xl bg-white border border-slate-200 hover:bg-rose-50 text-slate-800 hover:text-rose-600 text-xs font-bold flex items-center space-x-1 active:scale-95 transition-all shadow-2xs"
                >
                  <span>{ing}</span>
                  <span className="text-slate-400 hover:text-rose-500">×</span>
                </button>
              ))}
              {ingredients.length === 0 && (
                <span className="text-xs text-slate-400 p-1">No ingredients added yet</span>
              )}
            </div>

            {/* Add ingredient input */}
            <div className="flex items-center space-x-1.5">
              <input
                type="text"
                placeholder="Add ingredient..."
                value={newIngredientInput}
                onChange={(e) => setNewIngredientInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddIngredient();
                  }
                }}
                className="flex-1 px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-slate-400"
              />
              <button
                type="button"
                onClick={handleAddIngredient}
                disabled={!newIngredientInput.trim()}
                className="px-4 py-2 rounded-xl bg-slate-900 text-white font-bold text-xs active:scale-95 disabled:opacity-40 transition-all shadow-xs"
              >
                + Add
              </button>
            </div>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1">
                Date
              </label>
              <input
                type="date"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1">
                Time
              </label>
              <input
                type="time"
                value={entryTime}
                onChange={(e) => setEntryTime(e.target.value)}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:outline-none"
              />
            </div>
          </div>

          {/* Save Button */}
          <button
            type="submit"
            disabled={(!dishName.trim() && !descriptionPrompt.trim()) || isDecoding}
            className="w-full py-3.5 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white font-black text-xs uppercase tracking-wider shadow-md active:scale-[0.98] disabled:opacity-40 transition-all mt-2"
          >
            Save Meal
          </button>
        </form>
      </div>
    </div>
  );
};
